import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getDashboard, checkAllReplies, checkBounces } from '../api';
import toast from 'react-hot-toast';
import { Send, Users, CheckCircle, XCircle, MessageSquare, TrendingUp, Mail, FileText, RefreshCw, Filter, MailCheck, Clock, AlertTriangle, Download, List } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const params = {};
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      if (statusFilter) params.status = statusFilter;
      if (campaignFilter) params.campaignId = campaignFilter;
      setD((await getDashboard(params)).data);
    } catch (e) {
      if (e.response?.status === 503) toast.error('Database not connected');
    }
    setLoading(false);
  }, [dateFrom, dateTo, statusFilter, campaignFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const iv = setInterval(load, 8000); return () => clearInterval(iv); }, [load]);

  const handleCheckAll = async () => {
    setChecking(true);
    try {
      const r = (await checkAllReplies()).data;
      toast.success(r.message || 'Done');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Reply check failed'); }
    setChecking(false);
  };

  const [checkingBounce, setCheckingBounce] = useState(false);
  const handleCheckBounces = async () => {
    setCheckingBounce(true);
    try {
      const r = (await checkBounces()).data;
      toast.success(r.message || 'Done');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Bounce check failed'); }
    setCheckingBounce(false);
  };

  const clearFilters = () => { setDateFrom(''); setDateTo(''); setStatusFilter(''); setCampaignFilter(''); };

  if (loading) return <div className="pg-body" style={{ color: 'var(--txtm)' }}>Loading...</div>;
  if (!d) return <div className="pg-body"><p>Failed to load dashboard</p></div>;

  const pie = [
    { name: 'Delivered', value: d.totalDelivered, color: '#10b981' },
    { name: 'Sent', value: d.totalSent, color: '#3b82f6' },
    { name: 'Failed', value: d.totalFailed, color: '#ef4444' },
    { name: 'Bounced', value: d.totalBounced || 0, color: '#f97316' },
    { name: 'Replied', value: d.totalReplied, color: '#8b5cf6' },
    { name: 'Pending', value: d.totalPending, color: '#f59e0b' },
  ].filter(x => x.value > 0);

  const hasFilters = dateFrom || dateTo || statusFilter || campaignFilter;

  return (<>
    <div className="pg-hdr">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2>Dashboard</h2>
          <p>Real-time analytics {hasFilters && <span className="badge b-sent" style={{ marginLeft: 6 }}>Filtered</span>}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-s" onClick={handleCheckAll} disabled={checking}>
            <MailCheck size={15} /> {checking ? 'Checking...' : 'Check Replies'}
          </button>
          <button className="btn btn-s" onClick={handleCheckBounces} disabled={checkingBounce} style={{background:'var(--errD)',color:'var(--err)',border:'1px solid rgba(239,68,68,.2)'}}>
            <AlertTriangle size={15} /> {checkingBounce ? 'Scanning...' : 'Detect Bounces'}
          </button>
          <Link to="/email-logs" className="btn btn-g"><List size={15} /> All Logs</Link>
          <button className="btn btn-g" onClick={load}><RefreshCw size={15} /></button>
        </div>
      </div>
    </div>
    <div className="pg-body">

      {/* FILTERS */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-hdr" style={{ marginBottom: 8 }}>
          <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Filter size={13} /> Filters</span>
          {hasFilters && <button className="btn btn-g btn-sm" onClick={clearFilters}>Clear All</button>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
          <div><label className="lbl">From Date</label><input className="inp" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></div>
          <div><label className="lbl">To Date</label><input className="inp" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} /></div>
          <div><label className="lbl">Status</label>
            <select className="inp sel" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
              <option value="bounced">Bounced</option>
              <option value="replied">Replied</option>
            </select>
          </div>
          <div><label className="lbl">Campaign</label>
            <select className="inp sel" value={campaignFilter} onChange={e => setCampaignFilter(e.target.value)}>
              <option value="">All Campaigns</option>
              {(d.recentCampaigns || []).map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="stats">
        <Stat icon={<Send size={18} />} label="Campaigns" val={d.totalCampaigns} cls="si-a" />
        <Stat icon={<Users size={18} />} label="Contacts" val={d.totalContacts} cls="si-i" />
        <Stat icon={<Mail size={18} />} label="Sent" val={d.totalSent} cls="si-i" />
        <Stat icon={<CheckCircle size={18} />} label="Delivered" val={d.totalDelivered} cls="si-ok" />
        <Stat icon={<XCircle size={18} />} label="Failed" val={d.totalFailed} cls="si-err" />
        <Stat icon={<AlertTriangle size={18} />} label="Bounced" val={d.totalBounced||0} cls="si-err" />
        <Stat icon={<MessageSquare size={18} />} label="Replied" val={d.totalReplied} cls="si-r" />
        <Stat icon={<TrendingUp size={18} />} label="Active" val={d.activeSending} cls="si-w" />
      </div>

      {/* CHARTS */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, marginBottom: 14 }}>
        <div className="card">
          <div className="card-title" style={{ marginBottom: 10 }}>Daily Email Activity</div>
          {(d.dailyStats || []).length === 0 ? <div className="empty"><p>No data for selected period</p></div> : (
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={d.dailyStats}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#8b8a97' }} tickFormatter={v => v.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: '#8b8a97' }} />
                <Tooltip contentStyle={{ background: '#1a1a26', border: '1px solid #2a2a3a', borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="delivered" stackId="a" fill="#10b981" name="Delivered" />
                <Bar dataKey="failed" stackId="a" fill="#ef4444" name="Failed" />
                <Bar dataKey="replied" stackId="a" fill="#8b5cf6" name="Replied" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="card">
          <div className="card-title" style={{ marginBottom: 10 }}>Distribution</div>
          {pie.length === 0 ? <div className="empty"><p>No data</p></div> : (<>
            <ResponsiveContainer width="100%" height={170}>
              <PieChart><Pie data={pie} cx="50%" cy="50%" innerRadius={38} outerRadius={65} paddingAngle={3} dataKey="value">
                {pie.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie><Tooltip contentStyle={{ background: '#1a1a26', border: '1px solid #2a2a3a', borderRadius: 8, fontSize: 12 }} /></PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 4 }}>
              {pie.map((x, i) => <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10.5 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: x.color }} /><span style={{ color: 'var(--txt2)' }}>{x.name}: {x.value}</span>
              </div>)}
            </div>
          </>)}
        </div>
      </div>

      {/* CAMPAIGNS + REPLIES */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div className="card">
          <div className="card-hdr"><span className="card-title">Recent Campaigns</span><Link to="/campaigns" className="btn btn-g btn-sm">View All</Link></div>
          {(d.recentCampaigns || []).length === 0 ? <div className="empty"><Mail size={36} /><p>No campaigns</p></div> : (
            <table><thead><tr><th>Name</th><th>Status</th><th>Delivered</th><th>Failed</th><th>Replied</th></tr></thead>
              <tbody>{d.recentCampaigns.map(c => <tr key={c._id}>
                <td><Link to={`/campaigns/${c._id}`} style={{ color: 'var(--txt)', fontWeight: 500 }}>{c.name}</Link></td>
                <td><span className={`badge b-${c.status}`}>{c.status}</span></td>
                <td style={{ color: 'var(--ok)', fontFamily: 'var(--mono)' }}>{c.stats?.delivered || 0}</td>
                <td style={{ color: 'var(--err)', fontFamily: 'var(--mono)' }}>{c.stats?.failed || 0}</td>
                <td style={{ color: 'var(--reply)', fontFamily: 'var(--mono)' }}>{c.stats?.replied || 0}</td>
              </tr>)}</tbody>
            </table>
          )}
        </div>

        <div className="card">
          <div className="card-hdr">
            <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 5 }}><MessageSquare size={13} /> Recent Replies</span>
            <button className="btn btn-g btn-sm" onClick={handleCheckAll} disabled={checking}><MailCheck size={12} /> Refresh</button>
          </div>
          {(d.recentReplies || []).length === 0 ? (
            <div className="empty"><MessageSquare size={36} /><h3>No replies yet</h3><p style={{ fontSize: 11.5 }}>Click "Check All Replies" to fetch from your inbox via IMAP</p></div>
          ) : (
            <div style={{ maxHeight: 280, overflow: 'auto' }}>
              {d.recentReplies.map((r, i) => (
                <div key={r._id || i} style={{ padding: '9px 12px', borderBottom: '1px solid var(--bdr)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>{r.emailLogId?.toName || r.from}</div>
                    <div style={{ fontSize: 11, color: 'var(--txtm)' }}>{r.from}</div>
                    <div style={{ fontSize: 11, color: 'var(--txt2)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.body?.substring(0, 100) || r.subject || '(empty)'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <span className="badge b-replied">Reply</span>
                    <div style={{ fontSize: 10, color: 'var(--txtm)', marginTop: 3 }}>{new Date(r.createdAt).toLocaleDateString()}</div>
                    <div style={{ fontSize: 10, color: 'var(--txtm)' }}>{new Date(r.createdAt).toLocaleTimeString()}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  </>);
}

function Stat({ icon, label, val, cls }) {
  return <div className="stat"><div className={`stat-icon ${cls}`}>{icon}</div><div><h3>{val}</h3><p>{label}</p></div></div>;
}
